<template>
  <el-dialog
    title="用户设置"
    :visible.sync="dialogVisible"
    width="80%"
    :before-close="handleClose"
    class="user-settings-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div class="settings-container">
      <!-- 设置头部 -->
      <div class="settings-header">
        <div class="header-icon">
          <i class="el-icon-setting"></i>
        </div>
        <div class="header-content">
          <h2 class="header-title">个性化设置</h2>
          <p class="header-subtitle">根据您的学习习惯调整系统参数</p>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container" v-loading="loading">
        <p>加载设置中...</p>
      </div>

      <!-- 设置内容 -->
      <div v-else class="settings-content">
        <!-- 基本设置 -->
        <div class="settings-group" v-if="settingsByCategory.basic.length > 0">
          <div class="group-header">
            <div class="group-icon">
              <i class="el-icon-setting"></i>
            </div>
            <div class="group-info">
              <h3 class="group-title">基本设置</h3>
              <p class="group-desc">基础学习参数配置</p>
            </div>
          </div>
          <div class="settings-list">
            <div class="setting-item" v-for="setting in settingsByCategory.basic" :key="setting.id">
              <div class="setting-content">
                <div class="setting-label">
                  <h4 class="setting-name">{{ setting.description }}</h4>
                  <p class="setting-hint" v-if="getSettingDescription(setting.key)">
                    {{ getSettingDescription(setting.key) }}
                  </p>
                </div>
                <div class="setting-control">
                  <el-switch
                    v-if="setting.type === 'boolean'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    active-color="#409EFF"
                    inactive-color="#C0CCDA"
                  ></el-switch>
                  <el-select
                    v-else-if="setting.type === 'number' && setting.key.includes('words')"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请选择"
                    style="width: 120px"
                  >
                    <el-option
                      v-for="option in getWordOptions(setting.key)"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  <el-select
                    v-else-if="setting.type === 'number' && setting.key.includes('seconds')"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请选择"
                    style="width: 120px"
                  >
                    <el-option
                      v-for="option in getSecondsOptions()"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  <el-input
                    v-else
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请输入"
                    style="width: 150px"
                  ></el-input>
                </div>
              </div>
              <div class="setting-status" v-if="!setting.isEditable">
                <el-tooltip content="此设置不可编辑" placement="top">
                  <i class="el-icon-lock"></i>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- 高级设置 -->
        <div class="settings-group" v-if="settingsByCategory.advanced.length > 0">
          <div class="group-header">
            <div class="group-icon">
              <i class="el-icon-star-on"></i>
            </div>
            <div class="group-info">
              <h3 class="group-title">高级设置</h3>
              <p class="group-desc">高级学习参数配置</p>
            </div>
          </div>
          <div class="settings-list">
            <div class="setting-item" v-for="setting in settingsByCategory.advanced" :key="setting.id">
              <div class="setting-content">
                <div class="setting-label">
                  <h4 class="setting-name">{{ setting.description }}</h4>
                  <p class="setting-hint" v-if="getSettingDescription(setting.key)">
                    {{ getSettingDescription(setting.key) }}
                  </p>
                </div>
                <div class="setting-control">
                  <el-switch
                    v-if="setting.type === 'boolean'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    active-color="#409EFF"
                    inactive-color="#C0CCDA"
                  ></el-switch>
                  <el-select
                    v-else-if="setting.type === 'number'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请选择"
                    style="width: 120px"
                  >
                    <el-option
                      v-for="option in getNumberOptions(setting.key)"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  <el-input
                    v-else
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请输入"
                    style="width: 150px"
                  ></el-input>
                </div>
              </div>
              <div class="setting-status" v-if="!setting.isEditable">
                <el-tooltip content="此设置不可编辑" placement="top">
                  <i class="el-icon-lock"></i>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- 加强型学习 -->
        <div class="settings-group" v-if="settingsByCategory.enhanced.length > 0">
          <div class="group-header">
            <div class="group-icon">
              <i class="el-icon-trophy"></i>
            </div>
            <div class="group-info">
              <h3 class="group-title">加强型学习</h3>
              <p class="group-desc">四维学习法参数配置</p>
            </div>
          </div>
          <div class="settings-list">
            <div class="setting-item" v-for="setting in settingsByCategory.enhanced" :key="setting.id">
              <div class="setting-content">
                <div class="setting-label">
                  <h4 class="setting-name">{{ setting.description }}</h4>
                  <p class="setting-hint" v-if="getSettingDescription(setting.key)">
                    {{ getSettingDescription(setting.key) }}
                  </p>
                </div>
                <div class="setting-control">
                  <el-switch
                    v-if="setting.type === 'boolean'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    active-color="#409EFF"
                    inactive-color="#C0CCDA"
                  ></el-switch>
                  <el-select
                    v-else-if="setting.type === 'number'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请选择"
                    style="width: 120px"
                  >
                    <el-option
                      v-for="option in getNumberOptions(setting.key)"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  <el-input
                    v-else
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请输入"
                    style="width: 150px"
                  ></el-input>
                </div>
              </div>
              <div class="setting-status" v-if="!setting.isEditable">
                <el-tooltip content="此设置不可编辑" placement="top">
                  <i class="el-icon-lock"></i>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- 其它设置 -->
        <div class="settings-group" v-if="settingsByCategory.other.length > 0">
          <div class="group-header">
            <div class="group-icon">
              <i class="el-icon-connection"></i>
            </div>
            <div class="group-info">
              <h3 class="group-title">其它设置</h3>
              <p class="group-desc">系统配置和版本信息</p>
            </div>
          </div>
          <div class="settings-list">
            <div class="setting-item" v-for="setting in settingsByCategory.other" :key="setting.id">
              <div class="setting-content">
                <div class="setting-label">
                  <h4 class="setting-name">{{ setting.description }}</h4>
                  <p class="setting-hint" v-if="getSettingDescription(setting.key)">
                    {{ getSettingDescription(setting.key) }}
                  </p>
                </div>
                <div class="setting-control">
                  <el-select
                    v-if="setting.key === 'audio_server_region'"
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请选择区域"
                    style="width: 150px"
                  >
                    <el-option
                      v-for="option in getServerRegionOptions()"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    ></el-option>
                  </el-select>
                  <el-input
                    v-else
                    v-model="setting.value"
                    :disabled="!setting.isEditable"
                    placeholder="请输入"
                    style="width: 150px"
                  ></el-input>
                </div>
              </div>
              <div class="setting-status" v-if="!setting.isEditable">
                <el-tooltip content="此设置不可编辑" placement="top">
                  <i class="el-icon-lock"></i>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- 版本信息 -->
        <div class="version-section">
          <div class="version-card">
            <div class="version-header">
              <div class="version-icon">
                <i class="el-icon-info"></i>
              </div>
              <div class="version-info">
                <h4 class="version-title">系统信息</h4>
                <p class="version-desc">当前系统版本信息</p>
              </div>
            </div>
            <div class="version-content">
              <div class="version-item">
                <span class="version-label">版本号：</span>
                <span class="version-value">v3.9.1</span>
              </div>
              <div class="version-item">
                <span class="version-label">构建时间：</span>
                <span class="version-value">2025-01-20</span>
              </div>
              <div class="version-item">
                <span class="version-label">用户ID：</span>
                <span class="version-value">{{ userInfo.id || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleReset" :loading="resetLoading" icon="el-icon-refresh">
        重置默认
      </el-button>
      <el-button @click="handleClose" icon="el-icon-close">
        取消
      </el-button>
      <el-button type="primary" @click="handleSave" :loading="saveLoading" icon="el-icon-check">
        保存设置
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { UserSettingsAPI } from "@/api/userSettings";
import { mapState } from "vuex";

export default {
  name: "UserSettingsDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      settingsByCategory: {
        basic: [],
        advanced: [],
        enhanced: [],
        other: []
      },
      originalSettings: {},
      saveLoading: false,
      resetLoading: false
    };
  },
  computed: {
    ...mapState("user", ["userInfo"]),
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadSettings();
      }
    }
  },
  methods: {
    /**
     * 加载用户设置
     */
    async loadSettings() {
      try {
        this.loading = true;
        console.log('开始加载用户设置，用户信息:', this.userInfo);
        
        const response = await UserSettingsAPI.getUserSettings();
        console.log('用户设置API响应:', response);
        
        if (response.success) {
          // 按分类组织设置
          this.settingsByCategory = {
            basic: [],
            advanced: [],
            enhanced: [],
            other: []
          };
          
          if (response.data && Array.isArray(response.data)) {
            response.data.forEach(setting => {
              const category = setting.category || 'other';
              if (this.settingsByCategory[category]) {
                this.settingsByCategory[category].push(setting);
              }
            });
          }
          
          console.log('组织后的设置数据:', this.settingsByCategory);
          
          // 保存原始设置用于比较
          this.originalSettings = JSON.parse(JSON.stringify(this.settingsByCategory));
        } else {
          this.$message.error(response.message || '加载设置失败');
        }
      } catch (error) {
        console.error('加载设置失败：', error);
        this.$message.error('加载设置失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 保存设置
     */
    async handleSave() {
      try {
        this.saveLoading = true;
        
        // 收集所有设置
        const allSettings = [];
        Object.values(this.settingsByCategory).forEach(categorySettings => {
          categorySettings.forEach(setting => {
            if (setting.isEditable) {
              allSettings.push({
                key: setting.key,
                value: setting.value
              });
            }
          });
        });

        console.log('准备保存的设置:', allSettings);

        const response = await UserSettingsAPI.updateUserSettings(allSettings);
        if (response.success) {
          this.$message.success('设置保存成功');
          this.$emit('settings-saved');
          
          // 触发全局设置更新事件
          this.$root.$emit('settings-updated', this.settingsByCategory);
          
          this.handleClose();
        } else {
          this.$message.error(response.message || '保存设置失败');
        }
      } catch (error) {
        console.error('保存设置失败：', error);
        this.$message.error('保存设置失败');
      } finally {
        this.saveLoading = false;
      }
    },

    /**
     * 重置设置
     */
    async handleReset() {
      try {
        this.resetLoading = true;
        
        const response = await UserSettingsAPI.resetToDefault();
        if (response.success) {
          this.$message.success('设置已重置为默认值');
          this.loadSettings(); // 重新加载设置
        } else {
          this.$message.error(response.message || '重置设置失败');
        }
      } catch (error) {
        console.error('重置设置失败：', error);
        this.$message.error('重置设置失败');
      } finally {
        this.resetLoading = false;
      }
    },

    /**
     * 关闭对话框
     */
    handleClose() {
      this.$emit('update:visible', false);
    },

    /**
     * 获取单词数量选项
     */
    getWordOptions(key) {
      if (key === 'words_per_group') {
        return [
          { label: '5个', value: 5 },
          { label: '10个', value: 10 },
          { label: '15个', value: 15 },
          { label: '20个', value: 20 },
          { label: '30个', value: 30 }
        ];
      } else if (key === 'max_review_words') {
        return [
          { label: '10个', value: 10 },
          { label: '20个', value: 20 },
          { label: '30个', value: 30 },
          { label: '50个', value: 50 },
          { label: '100个', value: 100 }
        ];
      } else if (key === 'reverse_recall_threshold') {
        return [
          { label: '3个', value: 3 },
          { label: '5个', value: 5 },
          { label: '10个', value: 10 },
          { label: '15个', value: 15 },
          { label: '20个', value: 20 }
        ];
      }
      return [];
    },

    /**
     * 获取秒数选项
     */
    getSecondsOptions() {
      return [
        { label: '1秒', value: 1 },
        { label: '2秒', value: 2 },
        { label: '3秒', value: 3 },
        { label: '5秒', value: 5 },
        { label: '10秒', value: 10 }
      ];
    },

    /**
     * 获取数字选项
     */
    getNumberOptions(key) {
      if (key === 'word_pronunciation_times') {
        return [
          { label: '1次', value: 1 },
          { label: '2次', value: 2 },
          { label: '3次', value: 3 },
          { label: '5次', value: 5 }
        ];
      }
      return this.getWordOptions(key);
    },

    /**
     * 获取服务器区域选项
     */
    getServerRegionOptions() {
      return [
        { label: '华东区', value: 'east_china' },
        { label: '华北区', value: 'north_china' },
        { label: '华南区', value: 'south_china' },
        { label: '西南区', value: 'southwest_china' },
        { label: '东北区', value: 'northeast_china' }
      ];
    },

    /**
     * 获取设置描述
     */
    getSettingDescription(key) {
      const descriptions = {
        'auto_play_audio': '开启后会自动播放背景音乐，帮助提高学习专注力',
        'cancel_auto_review': '关闭自动复习功能，需要手动安排复习计划',
        'words_per_group': '每次学习时显示的单词数量，建议根据个人能力调整',
        'max_review_words': '单次复习的最大单词数量，避免过度疲劳',
        'correct_word_hint_seconds': '答对单词后显示提示的时间长度',
        'word_pronunciation_times': '每个单词的发音播放次数',
        'reverse_recall_threshold': '达到此数量后开始逆向回忆练习',
        'unit_end_recall': '完成一个学习单元后进行整体回忆测试',
        'audio_server_region': '选择距离最近的音频服务器，获得更好的播放体验',
        'background_music_url': '自定义背景音乐URL，留空则使用系统默认音乐'
      };
      return descriptions[key] || '';
    }
  }
};
</script>

<style scoped>
.user-settings-dialog .el-dialog__body {
  padding: 0;
}

.settings-container {
  max-height: 700px;
  overflow-y: auto;
}

.settings-header {
  display: flex;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #409EFF, #67C23A);
  color: white;
  margin-bottom: 0;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-right: 16px;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.header-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
}

.loading-container {
  padding: 40px;
  text-align: center;
}

.settings-content {
  padding: 24px;
}

.settings-group {
  margin-bottom: 32px;
}

.group-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 10px;
  border-left: 4px solid #409EFF;
}

.group-icon {
  width: 40px;
  height: 40px;
  background: #409EFF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-right: 16px;
}

.group-info {
  flex: 1;
}

.group-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 4px 0;
}

.group-desc {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.settings-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 16px;
}

.setting-item {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
}

.setting-item:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.setting-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.setting-label {
  flex: 1;
  min-width: 0;
}

.setting-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.setting-hint {
  font-size: 13px;
  color: #909399;
  margin: 0;
  line-height: 1.5;
}

.setting-control {
  flex-shrink: 0;
}

.setting-status {
  position: absolute;
  top: 16px;
  right: 16px;
  color: #C0CCDA;
  font-size: 16px;
}

.version-section {
  margin-top: 32px;
}

.version-card {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 24px;
}

.version-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
}

.version-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #909399, #606266);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.version-info {
  flex: 1;
}

.version-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 4px 0;
}

.version-desc {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.version-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.version-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.version-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.version-value {
  font-size: 14px;
  color: #303133;
  font-weight: 600;
}

.dialog-footer {
  padding: 20px;
  background: #f8f9fa;
  border-top: 1px solid #e4e7ed;
  text-align: right;
}

.dialog-footer .el-button {
  margin-left: 12px;
  padding: 12px 24px;
  font-size: 14px;
  border-radius: 8px;
  font-weight: 500;
}

.dialog-footer .el-button--primary {
  background: linear-gradient(135deg, #409EFF, #67C23A);
  border: none;
}

.dialog-footer .el-button--primary:hover {
  background: linear-gradient(135deg, #66b1ff, #85ce61);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .settings-list {
    grid-template-columns: 1fr;
  }
  
  .setting-item {
    padding: 16px;
  }
  
  .setting-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .setting-control {
    width: 100%;
  }
}
</style>

<!-- 非 scoped 样式：作用于被挂载到 body 的 el-dialog 元素 -->
<style>
/* Element UI 的 el-dialog 默认会 append 到 body，scoped 样式无法穿透。
   在此使用全局样式确保限制最大宽度 */
.user-settings-dialog .el-dialog,
.user-settings-dialog__wrapper,
.user-settings-dialog .el-dialog__wrapper {
  max-width: 1000px !important;
  width: 80% !important; /* 与模板 width="80%" 保持一致 */
}
</style>